import { makeAutoObservable } from "mobx";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  getCurrentUser,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword
} from "aws-amplify/auth";
import { v4 as uuidV4 } from "uuid";
import { request } from "../utils";

class AuthStore {
  constructor() {
    makeAutoObservable(this);
  }

  initialized = false;
  authDetermined = false;
  authenticated = false;
  user = {};
  groups = [];
  localCredentials = {};

  get sub() {
    return this.user?.attributes?.sub;
  }

  loading = false;

  async checkForAuthenticatedUser() {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const { "cognito:groups": groups } = session?.tokens?.accessToken?.payload || {};

      if (!groups?.includes("admin")) {
        console.warn("User is not in admin group, signing out");
        this.signOut();
        this.authenticated = false;
        this.authDetermined = true;
        return;
      }

      if (user) {
        this.authenticated = true;
        this.user = user;
        this.groups = groups;
        console.log("Authenticated user found:", user.username || user.userId);
      } else {
        this.authenticated = false;
        this.user = {};
      }
    } catch (err) {
      // This also means user isn't authenticated
      console.log("No authenticated user found:", err.message);
      this.authenticated = false;
    }

    this.authDetermined = true;

    // this.authenticated = true;
    // this.authDetermined = true;
  }

  async initializeAuth() {
    if (this.initialized) return;
    this.initialized = true;
    await this.checkForAuthenticatedUser();
  }

  async signIn(username, password) {
    this.loading = true;
    try {
      // Check if user is already signed in (Amplify v6 requirement)
      try {
        const existingUser = await getCurrentUser();
        if (existingUser) {
          // Already signed in, sign out first to avoid UserAlreadyAuthenticatedException
          await amplifySignOut();
          // Small delay to ensure sign out completes
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (err) {
        // No existing user, proceed with sign in
      }

      await amplifySignIn({ username: username || "-", password: password || "-" });
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const { "cognito:groups": groups } = session?.tokens?.accessToken?.payload || {};
      if (!groups?.includes("admin")) {
        this.signOut();
        this.loading = false;
        return { success: false };
      }

      this.user = user;
      this.groups = groups;
      this.authenticated = true;
      this.loading = false;
      return { success: true };
    } catch (err) {
      console.warn(err);
      this.authenticated = false;
      this.loading = false;
      return { success: false, error: err.message };
    }
  }

  async signUp({ firstName, lastName, email, phone, address1, address2, city, state, zip, password }) {
    this.loading = true;

    let success, error, lpmId;
    try {
      const username = uuidV4();
      await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
            phone_number: phone
          }
        }
      });
      const { id } = await request.post("/accounts", {
        body: {
          username,
          firstName,
          lastName,
          email,
          phone,
          address1,
          address2,
          city,
          state,
          zip
        }
      });
      success = true;
      lpmId = id;
      this.localCredentials = { lpmId, username, email, password };
    } catch (err) {
      console.warn(err);
      success = false;
      if (err.message === "PreSignUp failed with error A user with the same email address exists.") {
        error = "User account with the same email already exists. Please log in or reset password instead.";
      } else {
        error = err.message;
      }
    }

    this.loading = false;
    return { success, error, lpmId };
  }

  async resendVerificationCode() {
    let success, error;

    this.loading = true;
    try {
      await resendSignUpCode({ username: this.localCredentials?.username });
      success = true;
    } catch (err) {
      error = err.message;
      success = false;
    }

    this.loading = false;
    return { success, error };
  }

  async verifyAccount(code) {
    this.loading = true;
    try {
      const { username } = this.localCredentials || {};
      await amplifyConfirmSignUp({
        username,
        confirmationCode: code,
        options: { forceAliasCreation: true }
      });
      return { success: true };
    } catch (err) {
      this.loading = false;
      return { success: false, error: err.message };
    }
  }

  async sendResetPasswordCode(email) {
    let success, error;

    this.loading = true;
    try {
      await amplifyResetPassword({ username: email });
      success = true;
    } catch (err) {
      success = false;
      error = err.message;
    }

    this.loading = false;
    return { success, error };
  }

  async resetPassword(username, code, newPassword) {
    this.loading = true;
    let success, error;

    try {
      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword
      });
      success = true;
    } catch (err) {
      success = false;
      error = err.message;
    }

    this.loading = false;
    return { success, error };
  }

  async signOut() {
    try {
      await amplifySignOut();
      this.user = {};
      this.authenticated = false;
    } catch (err) {
      console.warn(err);
    }
  }
}

export default new AuthStore();
